import React from 'react';
import Modal from '@/components/layout/Modal';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Botao from '@/components/Botao';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoCheckbox from '@/components/Formulario/CampoCheckbox';
import CampoRadio from '@/components/Formulario/CampoRadio';
import { RadioGroup } from '@base-ui/react/radio-group';
import Badge, { BadgeVariants } from '@/components/layout/Badge';
import Icon from '@mdi/react';
import { mdiAlertOutline, mdiLoading } from '@mdi/js';
import { useMutation, useQuery } from 'react-query';
import axios from 'axios';
import { extractApiError } from '@/lib/apiError';
import { toastError, toastSuccess } from '@/lib/toast';
import { ApplianceDTO } from '@webip/shared/src/dto/appliance';
import type {
    ApplianceUpgradeCreateDTO,
    ApplianceUpgradeDTO,
    ApplianceUpgradeListDTO,
    ApplianceUpgradeStatusDTO,
} from '@webip/shared/src/dto/appliance_upgrade';

type Props = {
    appliance: ApplianceDTO | null;
    onClose: () => void;
};

const STATUS_META: Record<ApplianceUpgradeStatusDTO, { label: string; variant: BadgeVariants }> = {
    agendado: { label: 'Agendado', variant: 'info' },
    executando: { label: 'Executando', variant: 'warning' },
    reiniciando: { label: 'Reiniciando', variant: 'warning' },
    concluido: { label: 'Concluído', variant: 'success' },
    falhou: { label: 'Falhou', variant: 'danger' },
    cancelado: { label: 'Cancelado', variant: 'secondary' },
    expirado: { label: 'Expirado', variant: 'secondary' },
};

const TOLERANCIA_OPCOES = [
    { id: '30', label: '30 minutos' },
    { id: '60', label: '1 hora' },
    { id: '120', label: '2 horas' },
    { id: '240', label: '4 horas' },
];

const formatarDataHora = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

/** Valor de <input type="datetime-local"> no fuso do navegador. */
const paraDatetimeLocal = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Próximas 3 h da manhã — sugestão de janela de manutenção. */
const proximaMadrugada = () => {
    const d = new Date();
    d.setHours(3, 0, 0, 0);
    if (d.getTime() <= Date.now()) d.setDate(d.getDate() + 1);
    return d;
};

/** "25.7.5_2" → "25.7". */
const serie = (v: string | null | undefined) => /^(\d+\.\d+)/.exec(v ?? '')?.[1] ?? null;

const emExecucao = (u: ApplianceUpgradeDTO | null | undefined) =>
    u?.status === 'executando' || u?.status === 'reiniciando';

const UpgradeModal = ({ appliance, onClose }: Props) => {
    const applianceId = appliance?.id;
    const [modo, setModo] = React.useState<'agora' | 'agendar'>('agora');
    const [quando, setQuando] = React.useState(() => paraDatetimeLocal(proximaMadrugada()));
    const [tolerancia, setTolerancia] = React.useState('60');
    const [firmware, setFirmware] = React.useState(false);

    React.useEffect(() => {
        setModo('agora');
        setQuando(paraDatetimeLocal(proximaMadrugada()));
        setTolerancia('60');
        setFirmware(false);
    }, [applianceId]);

    const query = useQuery({
        queryKey: ['appliance-upgrade', applianceId],
        enabled: applianceId != null,
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; result: ApplianceUpgradeListDTO }>(
                `/api/appliance/${applianceId}/upgrade`,
                { withCredentials: true },
            );
            return res.data.result;
        },
        // Em execução acompanha de perto; agendado/ocioso, devagar.
        refetchInterval: (data) => (emExecucao(data?.ativo) ? 5_000 : 30_000),
    });

    const mutationAgendar = useMutation(
        async (body: ApplianceUpgradeCreateDTO) => {
            const res = await axios.post<{ ok: boolean; result: ApplianceUpgradeDTO }>(
                `/api/appliance/${applianceId}/upgrade`,
                body,
                { withCredentials: true },
            );
            return res.data.result;
        },
        {
            onSuccess: (u) => {
                toastSuccess(u.status === 'agendado'
                    ? `Atualização agendada para ${formatarDataHora(u.agendado_para)}`
                    : 'Atualização iniciada');
                query.refetch();
            },
            onError: (err) => toastError(extractApiError(err, 'Erro ao agendar atualização').message),
        },
    );

    const mutationCancelar = useMutation(
        async (upgradeId: number) => {
            await axios.post(`/api/appliance/${applianceId}/upgrade/${upgradeId}/cancel`, {}, { withCredentials: true });
        },
        {
            onSuccess: () => {
                toastSuccess('Agendamento cancelado');
                query.refetch();
            },
            onError: (err) => toastError(extractApiError(err, 'Erro ao cancelar agendamento').message),
        },
    );

    const handleConfirmar = () => {
        if (modo === 'agendar') {
            const data = new Date(quando);
            if (Number.isNaN(data.getTime())) {
                toastError('Informe a data e a hora do agendamento');
                return;
            }
            mutationAgendar.mutate({
                agendado_para: data.toISOString(),
                tolerancia_min: Number(tolerancia),
                atualizar_firmware: firmware,
            });
        } else {
            mutationAgendar.mutate({ agendado_para: null, atualizar_firmware: firmware });
        }
    };

    const dados = query.data;
    const opnsense = appliance?.agent_type === 'opnsense';
    const produto = dados?.produto ?? (opnsense ? 'OPNsense' : 'RouterOS');
    // OPNsense: alvo em outra série (25.7 → 26.1) é major upgrade — mais demorado e com mais risco.
    const trocaDeSerie = opnsense && !!dados?.pendente && serie(dados.pendente.versao_disponivel) !== serie(dados.pendente.versao_instalada);
    const ativo = dados?.ativo ?? null;
    const historico = (dados?.historico ?? []).filter((u) => u.id !== ativo?.id);

    return (
        <Modal
            show={appliance != null}
            title={`Atualizar ${produto}${appliance ? ` — ${appliance.nome}` : ''}`}
            onClose={onClose}
            hideControls
            size="lg"
        >
            <VBox style={{ gap: '20px' }}>
                {query.isLoading && (
                    <HBox style={{ gap: '8px', alignItems: 'center' }}>
                        <Icon path={mdiLoading} spin size="20px" /> Carregando…
                    </HBox>
                )}

                {dados && (
                    <div style={{ fontSize: '14px' }}>
                        {dados.pendente ? (
                            <>
                                Versão instalada <strong>{dados.pendente.versao_instalada ?? '?'}</strong>
                                {' · '}disponível{opnsense ? '' : ' no canal stable'} <strong>{dados.pendente.versao_disponivel}</strong>
                                {trocaDeSerie && <> (troca de série)</>}
                            </>
                        ) : (
                            <span style={{ color: 'var(--color-muted)' }}>
                                Nenhuma atualização pendente (pela última varredura de patches).
                            </span>
                        )}
                    </div>
                )}

                {ativo && (
                    <VBox style={{ gap: '8px', padding: '12px', borderRadius: '8px', background: 'var(--tc-color-gray-100)' }}>
                        <HBox style={{ gap: '8px', alignItems: 'center' }}>
                            <Badge variant={STATUS_META[ativo.status].variant}>{STATUS_META[ativo.status].label}</Badge>
                            <span style={{ fontSize: '14px' }}>
                                {ativo.versao_antes ?? '?'} → {ativo.versao_alvo}
                            </span>
                            {emExecucao(ativo) && <Icon path={mdiLoading} spin size="18px" />}
                        </HBox>
                        {ativo.status === 'agendado' && (
                            <span style={{ fontSize: '13px' }}>
                                Agendado para <strong>{formatarDataHora(ativo.agendado_para)}</strong>
                                {' '}(começa até {ativo.tolerancia_min} min depois, senão expira)
                            </span>
                        )}
                        {ativo.etapa && <span style={{ fontSize: '13px', color: 'var(--color-muted)' }}>{ativo.etapa}</span>}
                        {ativo.status === 'agendado' && (
                            <div>
                                <Botao
                                    size="sm"
                                    variant="danger"
                                    disabled={mutationCancelar.isLoading}
                                    onClick={() => mutationCancelar.mutate(ativo.id)}
                                >
                                    Cancelar agendamento
                                </Botao>
                            </div>
                        )}
                    </VBox>
                )}

                {dados && !ativo && dados.pendente && (
                    <VBox style={{ gap: '12px' }}>
                        {/* O Radio do base-ui só marca dentro de um RadioGroup — o
                            CampoRadio sozinho ignora `checked`. */}
                        <RadioGroup
                            value={modo}
                            onValueChange={(v) => setModo(v as 'agora' | 'agendar')}
                            style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                        >
                            <CampoRadio
                                id="upgrade-modo-agora"
                                value="agora"
                                label="Agora"
                                checked={modo === 'agora'}
                                onChange={() => setModo('agora')}
                            />
                            <CampoRadio
                                id="upgrade-modo-agendar"
                                value="agendar"
                                label="Agendar"
                                checked={modo === 'agendar'}
                                onChange={() => setModo('agendar')}
                            />
                        </RadioGroup>

                        {modo === 'agendar' && (
                            <HBox style={{ gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                <div style={{ minWidth: '220px' }}>
                                    <CampoTexto
                                        label="Data e hora"
                                        type="datetime-local"
                                        value={quando}
                                        onChange={setQuando}
                                    />
                                </div>
                                <div style={{ minWidth: '180px' }}>
                                    <CampoSeletor
                                        label="Tolerância"
                                        value={tolerancia}
                                        onChange={(v) => setTolerancia((v as string) ?? '60')}
                                        options={TOLERANCIA_OPCOES}
                                        noSearch
                                    />
                                </div>
                            </HBox>
                        )}

                        {!opnsense && (
                            <CampoCheckbox
                                text="Atualizar também o firmware RouterBOARD (reinicia uma segunda vez)"
                                value={firmware}
                                onChange={setFirmware}
                            />
                        )}

                        <HBox style={{ gap: '8px', alignItems: 'flex-start', fontSize: '13px', color: 'var(--tc-color-warning-700, #b45309)' }}>
                            <Icon path={mdiAlertOutline} size="18px" style={{ flexShrink: 0 }} />
                            <span>
                                {opnsense
                                    ? <>O firewall reinicia e a rede fica fora do ar por alguns minutos.{trocaDeSerie && ' A troca de série é mais demorada e pode exigir mais de um reinício — confira a compatibilidade dos plugins instalados.'}{' '}</>
                                    : <>O router reinicia e a rede fica fora do ar por alguns minutos. Antes de atualizar, o agente salva um backup no próprio router.{' '}</>}
                                Se o agente estiver offline no horário, a atualização espera até a tolerância e depois expira.
                            </span>
                        </HBox>

                        <div>
                            <Botao
                                variant={modo === 'agora' ? 'warning' : 'primary'}
                                disabled={mutationAgendar.isLoading}
                                onClick={handleConfirmar}
                            >
                                {modo === 'agora' ? 'Atualizar agora' : 'Agendar atualização'}
                            </Botao>
                        </div>
                    </VBox>
                )}

                {historico.length > 0 && (
                    <VBox style={{ gap: '6px' }}>
                        <strong style={{ fontSize: '14px' }}>Histórico</strong>
                        {historico.map((u) => (
                            <HBox key={u.id} style={{ gap: '8px', alignItems: 'baseline', flexWrap: 'wrap', fontSize: '13px' }}>
                                <Badge variant={STATUS_META[u.status].variant}>{STATUS_META[u.status].label}</Badge>
                                <span>{formatarDataHora(u.concluido_em ?? u.agendado_para)}</span>
                                <span>
                                    {u.versao_antes ?? '?'} → {u.versao_depois ?? u.versao_alvo}
                                </span>
                                {u.erro && <span style={{ color: 'var(--color-muted)' }}>{u.erro}</span>}
                            </HBox>
                        ))}
                    </VBox>
                )}
            </VBox>
        </Modal>
    );
};

export default UpgradeModal;
